import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getFirebaseToken } from "../utils/getFirebaseToken";
import {
  addSupplierItemPrice,
  fetchSupplierItemById,
  fetchSupplierItemPrices,
  updateSupplierItemPrice,
} from "../services/erpSupplierItems";

function emptyPriceForm() {
  return {
    valid_from: "",
    valid_to: "",
    price: 0,
    discount_percent: 0,
    net_price: "",
    currency: "RSD",
    availability_qty: "",
    lead_time_days: "",
    price_source: "manual",
  };
}

function toDateTimeInputValue(value) {
  if (!value) return "";
  return String(value).slice(0, 16);
}

export default function SupplierItemPricesPage() {
  const { id } = useParams();

  const [item, setItem] = useState(null);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyPriceForm());
  const [editingPriceId, setEditingPriceId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getFirebaseToken();
      const [itemRes, pricesRes] = await Promise.all([
        fetchSupplierItemById(token, id),
        fetchSupplierItemPrices(token, id),
      ]);

      setItem(itemRes.data || null);
      setRows(pricesRes.rows || []);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e.message || "Failed to load prices."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const token = await getFirebaseToken();

      const payload = {
        ...form,
        valid_from: form.valid_from ? `${form.valid_from}:00` : null,
        valid_to: form.valid_to ? `${form.valid_to}:00` : null,
        price: Number(form.price || 0),
        discount_percent: Number(form.discount_percent || 0),
        net_price:
          form.net_price === "" || form.net_price === null
            ? null
            : Number(form.net_price),
        availability_qty:
          form.availability_qty === "" ? null : Number(form.availability_qty),
        lead_time_days:
          form.lead_time_days === "" ? null : Number(form.lead_time_days),
      };

      if (editingPriceId) {
        await updateSupplierItemPrice(token, editingPriceId, payload);
      } else {
        await addSupplierItemPrice(token, id, payload);
      }

      setForm(emptyPriceForm());
      setEditingPriceId(null);
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const editRow = (row) => {
    setEditingPriceId(row.id);
    setForm({
      valid_from: toDateTimeInputValue(row.valid_from),
      valid_to: toDateTimeInputValue(row.valid_to),
      price: row.price ?? 0,
      discount_percent: row.discount_percent ?? 0,
      net_price: row.net_price ?? "",
      currency: row.currency || "RSD",
      availability_qty: row.availability_qty ?? "",
      lead_time_days: row.lead_time_days ?? "",
      price_source: row.price_source || "manual",
    });
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Supplier Price History</h1>
        <p className="text-slate-600">
          Maintain supplier prices over time for sourcing and procurement.
        </p>
      </div>

      {item ? (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="font-semibold">
            {item.item_sku} - {item.item_name}
          </div>
          <div className="text-slate-600">
            Supplier SKU: {item.supplier_sku} | Supplier: {item.partner_name || "-"} | Source:{" "}
            {item.source_name || "-"}
          </div>
        </div>
      ) : null}

      <div className="bg-white rounded-xl shadow-sm border p-4 space-y-4">
        <div className="grid gap-3 md:grid-cols-4">
          <input
            type="datetime-local"
            value={form.valid_from}
            onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="datetime-local"
            value={form.valid_to}
            onChange={(e) => setForm({ ...form, valid_to: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="number"
            step="0.0001"
            placeholder="Gross price"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="number"
            step="0.0001"
            placeholder="Discount %"
            value={form.discount_percent}
            onChange={(e) =>
              setForm({ ...form, discount_percent: e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-4">
          <input
            type="number"
            step="0.0001"
            placeholder="Net price (optional)"
            value={form.net_price}
            onChange={(e) => setForm({ ...form, net_price: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="text"
            placeholder="Currency"
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="number"
            step="0.0001"
            placeholder="Availability qty"
            value={form.availability_qty}
            onChange={(e) =>
              setForm({ ...form, availability_qty: e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="number"
            placeholder="Lead time days"
            value={form.lead_time_days}
            onChange={(e) =>
              setForm({ ...form, lead_time_days: e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <input
            type="text"
            placeholder="Price source"
            value={form.price_source}
            onChange={(e) => setForm({ ...form, price_source: e.target.value })}
            className="border rounded-lg px-3 py-2"
          />

          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-black text-white"
            >
              {saving ? "Saving..." : editingPriceId ? "Update Price" : "Add Price"}
            </button>

            {editingPriceId ? (
              <button
                type="button"
                onClick={() => {
                  setEditingPriceId(null);
                  setForm(emptyPriceForm());
                }}
                className="px-4 py-2 rounded-lg border"
              >
                Cancel Edit
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
        </div>
      ) : null}

      <div className="bg-white rounded-xl shadow-sm border overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-3 py-3 text-left">Valid From</th>
              <th className="px-3 py-3 text-left">Valid To</th>
              <th className="px-3 py-3 text-right">Price</th>
              <th className="px-3 py-3 text-right">Discount %</th>
              <th className="px-3 py-3 text-right">Net Price</th>
              <th className="px-3 py-3 text-left">Currency</th>
              <th className="px-3 py-3 text-right">Availability</th>
              <th className="px-3 py-3 text-right">Lead Time</th>
              <th className="px-3 py-3 text-left">Source</th>
              <th className="px-3 py-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-slate-500">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-slate-500">
                  No prices found.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-3">{String(row.valid_from || "").slice(0, 16)}</td>
                  <td className="px-3 py-3">{String(row.valid_to || "").slice(0, 16)}</td>
                  <td className="px-3 py-3 text-right">{Number(row.price || 0).toFixed(4)}</td>
                  <td className="px-3 py-3 text-right">
                    {Number(row.discount_percent || 0).toFixed(4)}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {Number(row.net_price || 0).toFixed(4)}
                  </td>
                  <td className="px-3 py-3">{row.currency}</td>
                  <td className="px-3 py-3 text-right">
                    {row.availability_qty == null
                      ? "-"
                      : Number(row.availability_qty).toFixed(4)}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {row.lead_time_days == null ? "-" : row.lead_time_days}
                  </td>
                  <td className="px-3 py-3">{row.price_source || "-"}</td>
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      onClick={() => editRow(row)}
                      className="px-3 py-1.5 rounded-lg border"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}